import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import type { ITfcV1RequestAddEditCustomControlData } from './TfcV1RequestAddEdit';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormDivider, FormParagraph, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { NullableFormattedDate } from '../../../../elements/NullableFormattedDate';
import { ProcessContainer, ProfileImageWithName } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { OphthalmologyDropzoneRead } from '../../TFO/v1/OphthalmologyDropzoneRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { ServiceConsumerSupervisorRead } from '../../utils/v1/ServiceConsumerSupervisorRead';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';

interface ITfcV1RequestReadCustomControlData extends ITfcV1RequestAddEditCustomControlData {
    vraag: string;
}

export function TfcV1RequestRead(props: ICustomControlRequestReadProps) {
    const customControlData: ITfcV1RequestReadCustomControlData = props.request.customControlData;

    const question =
        props.request.customControlData && props.request.customControlData.vraag
            ? props.request.customControlData.vraag
            : props.request.question;

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.request.status !== RequestStatus.requested ? (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('request.sendTo')}</FormLabel>
                        <InputContainer>
                            <ProfileImageWithName
                                organization={props.request.recipientOrganization}
                                practitioner={props.request.recipientPractitioner}
                            />
                        </InputContainer>
                    </HorizontalFormGroupRead>
                ) : (
                    <ProfileImages request={props.request} />
                )}

                <ServiceConsumerSupervisorRead {...props} />

                <FormDivider />

                {customControlData && customControlData.files && customControlData.files.length > 0 && (
                    <>
                        <FormDivider />
                        <OphthalmologyDropzoneRead
                            files={customControlData.files}
                            id={props.request.id}
                            urlBase={FilesUrlBase.requests}
                        />
                        <FormDivider />
                    </>
                )}
                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList requestId={props.request.id} />

                {customControlData && customControlData.gereguleerd && (
                    <HorizontalFormGroupRead>
                        <FormLabel> Patiënt goed gereguleerd </FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{customControlData.gereguleerd}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.diabetes_sinds && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Diabetes sinds</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>
                                <NullableFormattedDate value={customControlData.diabetes_sinds} />
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <Icd10SelectionsRead
                    diagnosis={props.request.diagnosis}
                    diagnosisConclusion={props.request.customControlData?.conclusie_diagnose}
                    hideTitle
                    showWorkingDiagnosis
                    thesaurusTermDate={props.request.dateSent}
                />

                {question && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Vraag</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{question}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.toelichting && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.toelichting}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <RequestReadButtons {...props} />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideConsult')}
                    labelMore={translateKey('request.showConsult')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}
